import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  FaSchool,
  FaUsersCog,
  FaMoneyBill,
  FaUserShield,
  FaPlus,
  FaEye,
} from "react-icons/fa";
import api from "../../api";

const SuperAdminDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    schools: 0,
    admins: 0,
    subscriptions: 0,
    revenue: 0,
  });

  useEffect(() => {
    let aborted = false;
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      let aborted = false;

      try {
        const res = await api.get("/super_admin/dashboard/get_data");

        if (res.data?.status && !aborted) {
          setData(res.data.data);
        }
      } catch (err) {
        console.error("SuperAdmin Dashboard fetch error:", err);
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      aborted = true;
      controller.abort();
    };
  }, []);

  // 👉 Stats cards
  const statsCards = [
    {
      id: "schools",
      label: "Total Schools",
      icon: <FaSchool />,
      color: "#1e88e5",
      value: data.schools,
      routeOnCard: "/super_admin/schools",
      routeOnButton: "/super_admin/schools",
      buttonLabel: "Add School",
      buttonIcon: <FaPlus />,
    },
    {
      id: "admins",
      label: "Admins & Staff",
      icon: <FaUsersCog />,
      color: "#43a047",
      value: data.admins,
      routeOnCard: "/super_admin/schooladmins",
      routeOnButton: "/super_admin/schooladmins",
      buttonLabel: "Manage",
      buttonIcon: <FaEye />,
    },
    {
      id: "subscriptions",
      label: "Active Subscriptions",
      icon: <FaUserShield />,
      color: "#fb8c00",
      value: data.subscriptions,
      routeOnCard: "/super_admin/subscriptions",
      routeOnButton: "/super_admin/subscriptions",
      buttonLabel: "Renewals",
      buttonIcon: <FaEye />,
    },
    {
      id: "revenue",
      label: "Total Revenue",
      icon: <FaMoneyBill />,
      color: "#8e24aa",
      value: `₹ ${new Intl.NumberFormat("en-IN").format(data.revenue)}`,
      routeOnCard: "/super_admin/subscriptions",
      routeOnButton: "/super_admin/subscriptions",
      buttonLabel: "View All",
      buttonIcon: <FaEye />,
    },
  ];

  // 👉 Management menu cards
  const menuCards = [
    {
      id: "schools",
      label: "Schools",
      icon: <FaSchool />,
      color: "#1e88e5",
      routeOnCard: "/super_admin/schools",
    },
    {
      id: "subscriptions",
      label: "Subscriptions",
      icon: <FaUserShield />,
      color: "#fb8c00",
      routeOnCard: "/super_admin/subscriptions",
    },
    {
      id: "admins",
      label: "Admins & Staff",
      icon: <FaUsersCog />,
      color: "#43a047",
      routeOnCard: "/super_admin/schooladmins",
    },
  ];

  return (
    <Box>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 260,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Stats Cards */}
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Platform Overview</Typography>
          <Grid container spacing={3}>
            {statsCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
                <Paper
                  elevation={3}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    borderRadius: 2,
                    minHeight: 110,
                    transition: "transform .18s ease, box-shadow .18s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: 8 },
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(card.routeOnCard)}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      background: `${card.color}22`,
                    }}
                  >
                    <Box sx={{ color: card.color, fontSize: 28 }}>{card.icon}</Box>
                  </Box>
                  <Box sx={{ flex: 1, px: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: "text.secondary" }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 22 }}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(card.routeOnButton);
                    }}
                    startIcon={card.buttonIcon}
                    sx={{
                      background: `linear-gradient(90deg, ${card.color}, ${card.color}cc)`,
                      color: "white",
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.8,
                    }}
                  >
                    {card.buttonLabel}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Menu Cards */}
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Management</Typography>
          <Grid container spacing={3}>
            {menuCards.map((m) => (
              <Grid item xs={12} sm={6} md={4} key={m.id}>
                <Paper
                  elevation={2}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    borderRadius: 2,
                    textAlign: "center",
                    transition: "all .2s ease",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: 6 },
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(m.routeOnCard)}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: `${m.color}22`,
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ color: m.color, fontSize: 28 }}>{m.icon}</Box>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                    {m.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default SuperAdminDashboard;
